import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { css } from "@nju/css";
import {
  IFtthOrchestrator,
  IModemReturnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { IPayment } from "@nju/data-access-portal-payment";
import {
  IAccess,
  IModem,
  IServiceStatus,
} from "@nju/data-access-portal-product";
import { useMaintenance } from "@nju/data-access-portal-technical";
import { portalRoutes } from "@nju/routes";
import { A, Box, Button } from "@nju/ui";
import { FtthReactivationButton } from "./FtthReactivationButton";
import { IServiceCTABannerConditions } from "./types";

interface IButtonsProps {
  subscribeCallback: (status?: IServiceStatus["ftthServiceStatus"]) => void;
  refreshCallback: () => void;
  retryPaymentCallback: () => void;
  conditions: IServiceCTABannerConditions;
  anniversaryDate: string | undefined;
  access: IAccess | undefined;
  modemProducts: IModem | undefined;
  payment: IPayment | undefined;
  ftthServiceStatus: IServiceStatus["ftthServiceStatus"];
  modemReturnOrchestrator: IModemReturnOrchestrator | undefined;
  ftthOrchestrator: IFtthOrchestrator | undefined;
}

export function Buttons({
  access,
  modemProducts,
  conditions,
  ftthServiceStatus,
  subscribeCallback,
  refreshCallback,
  retryPaymentCallback,
  modemReturnOrchestrator,
  anniversaryDate,
  ftthOrchestrator,
  payment,
}: IButtonsProps) {
  const router = useRouter();
  const { paymentsUnavailable } = useMaintenance();
  const {
    isPaymentInProgress,
    isPaymentError,
    isBMPaymentPending,
    isDWPaymentPending,
    isTimeForPaymentWithProducts,
    isTimeForPaymentWithoutProducts,
    isUnsubscribed,
    isNoPayment,
    isAlmostTerminated,
    isDisconnected,
    isSuspended,
    isPendingReactivation,
    isPendingTermination,
    isTermination,
    isPending,
    haveToPaidBeforeOrder,
    isOrderProcessAvailable,
    isUnreturnedModem,
  } = conditions;

  const handlePayment = React.useCallback(() => {
    if (paymentsUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      router.push(portalRoutes.mojeKonto.platnosci.zaplac.get());
    }
  }, [router, paymentsUnavailable]);

  if (isPaymentInProgress) {
    return (
      <Button
        onPress={retryPaymentCallback}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        odśwież
      </Button>
    );
  }

  if (isDWPaymentPending) {
    return (
      <Box className={styles.buttonsWrapper()} display="flex">
        <Link
          href={portalRoutes.mojeKonto.platnosci.index.get()}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            variant="secondary"
          >
            inna metoda płatności
          </A>
        </Link>
        <Button
          onPress={refreshCallback}
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          odśwież
        </Button>
      </Box>
    );
  }

  if (isPaymentError) {
    return (
      <Box className={styles.buttonsWrapper()} display="flex">
        <Link
          href={portalRoutes.mojeKonto.platnosci.index.get()}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            variant="secondary"
          >
            zarządzaj płatnością
          </A>
        </Link>
        <Button
          onPress={retryPaymentCallback}
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          ponów płatność
        </Button>
      </Box>
    );
  }

  if (isBMPaymentPending && payment?.transactionLink) {
    return (
      <Box className={styles.buttonsWrapper()} display="flex">
        <Link
          href={portalRoutes.mojeKonto.platnosci.index.get()}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            variant="secondary"
          >
            inna metoda płatności
          </A>
        </Link>
        <Link href={payment.transactionLink} passHref={true}>
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            wróć do płatności
          </A>
        </Link>
      </Box>
    );
  }

  if (isPaymentInProgress) {
    return (
      <Button
        onPress={() => {
          return retryPaymentCallback();
        }}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        odśwież
      </Button>
    );
  }

  if (isTimeForPaymentWithoutProducts) {
    return (
      <Button
        onPress={handlePayment}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        zapłać
      </Button>
    );
  }

  if (
    isTimeForPaymentWithProducts &&
    ftthOrchestrator?.paymentStartDate &&
    ftthOrchestrator?.orderSummary?.totalOrderPrice
  ) {
    return (
      <Button
        onPress={handlePayment}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        zapłać
      </Button>
    );
  }

  if (isUnsubscribed && anniversaryDate) {
    return (
      <Button
        onPress={() => {
          return subscribeCallback(ftthServiceStatus);
        }}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        anuluj zawieszenie usługi
      </Button>
    );
  }

  if (isNoPayment && access?.statusChangeDate) {
    return (
      <Link
        href={portalRoutes.mojeKonto.platnosci.historia.get({
          ostatniaNieudanaPlatnosc: "1",
        })}
        passHref={true}
      >
        <A
          display="inlineBlock"
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          szczegóły płatności
        </A>
      </Link>
    );
  }

  if (
    isAlmostTerminated &&
    (access?.terminationDate || access?.statusChangeDate)
  ) {
    return <FtthReactivationButton />;
  }

  if (isDisconnected && access?.statusChangeDate) {
    return (
      <Link
        href={portalRoutes.mojeKonto.mojeUslugi.reaktywacja.index.get({
          mode: "ftth",
        })}
        passHref={true}
      >
        <A
          display="inlineBlock"
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          przywróć usługę
        </A>
      </Link>
    );
  }

  if (isSuspended && (access?.terminationDate || access?.statusChangeDate)) {
    return <FtthReactivationButton />;
  }

  if (isPendingReactivation) {
    return (
      <Button
        onPress={() => {
          return refreshCallback();
        }}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        odśwież
      </Button>
    );
  }

  if (
    isPendingTermination &&
    access?.anniversaryDate &&
    modemProducts?.characteristic?.serialNumber
  ) {
    return (
      <Button
        onPress={() => {
          return subscribeCallback(ftthServiceStatus);
        }}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        anuluj zakończenie usługi
      </Button>
    );
  }

  if (isUnreturnedModem && modemReturnOrchestrator?.contractPenaltyFee) {
    return (
      <Box className={styles.buttonsWrapper()} display="flex">
        <Link
          href={portalRoutes.mojeKonto.pomoc.faq.get({
            category: "zarzadzanie-kontem-i-modemem",
            article: "jak-zwrócę-modem-przy-odstąpieniu-od-umowy?",
          })}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            variant="secondary"
          >
            jak zwrócić modem?
          </A>
        </Link>
        <Link
          href={portalRoutes.mojeKonto.platnosci.historia.get({
            ostatniaNieudanaPlatnosc: "1",
          })}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
          >
            szczegóły płatności
          </A>
        </Link>
      </Box>
    );
  }

  if (haveToPaidBeforeOrder) {
    return (
      <Link
        href={portalRoutes.mojeKonto.platnosci.historia.get({
          ostatniaNieudanaPlatnosc: "1",
        })}
        passHref={true}
      >
        <A
          display="inlineBlock"
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          szczegóły płatności
        </A>
      </Link>
    );
  }

  if (
    isTermination &&
    modemReturnOrchestrator?.modemSerialNumber &&
    modemReturnOrchestrator?.caseContext === "14CANCELLATION"
  ) {
    return (
      <Link
        href={portalRoutes.mojeKonto.pomoc.faq.get({
          category: "zarzadzanie-kontem-i-modemem",
          article: "jak-zwrócę-modem-przy-odstąpieniu-od-umowy?",
        })}
        passHref={true}
      >
        <A
          display={{
            "@onlyMobile": "flex",
            "@fromTablet": "inlineBlock",
          }}
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          jak zwrócić modem?
        </A>
      </Link>
    );
  }

  if (
    isTermination &&
    modemReturnOrchestrator?.modemSerialNumber &&
    modemReturnOrchestrator?.caseContext === "TERMINATION" &&
    modemReturnOrchestrator?.contractTerminationDate
  ) {
    return (
      <Link
        href={portalRoutes.mojeKonto.pomoc.faq.get({
          category: "zarzadzanie-kontem-i-modemem",
          article: "jak-zwrócę-modem-przy-odstąpieniu-od-umowy?",
        })}
        passHref={true}
      >
        <A
          display={{
            "@onlyMobile": "flex",
            "@fromTablet": "inlineBlock",
          }}
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          jak zwrócić modem?
        </A>
      </Link>
    );
  }

  if (isOrderProcessAvailable) {
    return (
      <Link href={portalRoutes.sprawdzDostepnosc.index.get()} passHref={true}>
        <A
          display="inlineBlock"
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "m",
          }}
        >
          kup usługę
        </A>
      </Link>
    );
  }

  if (isPending) {
    return (
      <Button
        onPress={() => {
          return refreshCallback();
        }}
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "m",
        }}
      >
        odśwież
      </Button>
    );
  }

  // eslint-disable-next-line unicorn/no-null
  return null;
}

const styles = {
  buttonsWrapper: css({
    flexDirection: "column-reverse",
    gap: "$15",
    "@fromTablet": {
      flexDirection: "row",
    },
  }),
};
